import styled from '@emotion/styled';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const ContentBox = styled.div<{ clamped: boolean }>`
    position: relative;
    ${({ clamped }) =>
        clamped
            ? `
        &::after {
            content: '';
            position: absolute;
            left: 0;
            right: 0;
            bottom: 0;
            height: 1.75em;
            background: linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.95));
            pointer-events: none;
        }
    `
            : ''}/* background: #f8f8f8; */
`;

const Content = styled.div<{ clamped: boolean; lines: number }>`
    ${({ clamped, lines }) =>
        clamped
            ? `
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: ${lines};
        overflow: hidden;
    `
            : ''}
`;

const ToggleRow = styled.div`
    display: flex;
    justify-content: center;
`;

const Toggle = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: none;
    border: none;
    padding: 0;
    color: #2563eb;
    font-family: inherit;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    &:hover {
        color: #1d4ed8;
    }
    &:focus-visible {
        outline: 2px solid #93c5fd;
        outline-offset: 2px;
        border-radius: 2px;
    }
`;

const Caret = styled.span<{ up: boolean }>`
    display: inline-block;
    transition: transform 120ms ease-in-out;
    transform: rotate(${({ up }) => (up ? '180deg' : '0deg')});
    font-size: 10px;
    line-height: 1;
`;

interface ClampedTextProps {
    children: React.ReactNode;
    lines?: number;
    /** When provided, expansion is driven externally and the built-in toggle is hidden. */
    expanded?: boolean;
}

export const ClampedText = ({ children, lines = 3, expanded: expandedProp }: ClampedTextProps) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const isControlled = expandedProp !== undefined;
    const [internalExpanded, setInternalExpanded] = useState(false);
    const expanded = isControlled ? expandedProp : internalExpanded;
    const [overflows, setOverflows] = useState(false);

    useLayoutEffect(() => {
        const node = contentRef.current;
        if (!node) return;

        const check = () => {
            // When expanded, we can't measure overflow directly; temporarily
            // clamp to determine whether the toggle should remain.
            if (expanded) return;
            setOverflows(node.scrollHeight > node.clientHeight + 1);
        };
        check();

        const observer = new ResizeObserver(check);
        observer.observe(node);
        return () => observer.disconnect();
    }, [children, lines, expanded]);

    useEffect(() => {
        // Re-measure on window resize (font swap, layout shift).
        const onResize = () => {
            const node = contentRef.current;
            if (!node || expanded) return;
            setOverflows(node.scrollHeight > node.clientHeight + 1);
        };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [expanded]);

    const showFade = overflows && !expanded;

    return (
        <Wrapper>
            <ContentBox clamped={showFade}>
                <Content ref={contentRef} clamped={!expanded} lines={lines}>
                    {children}
                </Content>
            </ContentBox>
            {overflows && !isControlled ? (
                <ToggleRow>
                    <Toggle
                        type="button"
                        onClick={() => setInternalExpanded((v) => !v)}
                        data-testid="clamped-text-toggle"
                    >
                        {expanded ? 'Show less' : 'Show more'}
                        <Caret up={expanded} aria-hidden>
                            ▾
                        </Caret>
                    </Toggle>
                </ToggleRow>
            ) : null}
        </Wrapper>
    );
};
